<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Button as UiButton, LoadingButton } from '@/components/ui/button'
import Recommended from '../RecommendedSection.vue'
import Heading from '../Heading.vue'
import LoginForm from './BaseLoginForm.vue'
import ArrowImport from '@/assets/icons/arrow-import.svg?component'
import { ref } from 'vue'

const props = defineProps<{
  email: string
  onSendCode: (email: string) => Promise<void>
  onEmailChange: (email: string) => void
  onKeepPracticing?: () => void
  onContinueWithoutSaving?: () => void
  isCodeSent: boolean
  countdown: number
  errors: Record<string, string>
  subTitle?: string
}>()

const isLoading = ref(false)

const handleSendCode = async (email: string) => {
  isLoading.value = true
  try {
    await props.onSendCode(email)
  } catch (error) {
    console.error('Error sending code:', error)
  } finally {
    isLoading.value = false
  }
}

const { t } = useI18n()
</script>

<template>
  <div>
    <Heading
      :title="t('LoginPopup.label.CreateAnAccount')"
      :subTitle="t('LoginPopup.label.RecommendedForBestExperience')"
      class="pt-16 lg:pt-20"
    />

    <Recommended class="mb-5 mt-10" />

    <LoginForm
      :email="email"
      :onEmailChange="onEmailChange"
      :isCodeSent="isCodeSent"
      :countdown="countdown"
      :errors="errors"
      @submit="handleSendCode"
    >
      <template #buttons="{ disabled }">
        <LoadingButton
          variant="primary-homepage"
          :disabled="disabled"
          :loading="isLoading"
          class="w-full"
          type="submit"
        >
          {{ t('LoginPopup.button.SendVerificationCode') }}
        </LoadingButton>
      </template>

      <template #footer>
        <div class="mt-2 flex flex-col gap-2 text-center lg:mb-10 lg:mt-4">
          <span
            class="mt-4 text-sm font-light text-white/70 lg:mt-8"
            v-if="props.onContinueWithoutSaving"
          >
            {{ t('LoginPopup.label.ContinueWithoutSaving') }}
          </span>
          <button
            class="relative h-[48px] w-full cursor-pointer rounded-[8px] border-[0.5px] border-white/10 bg-[#19233A] text-sm text-white/70"
            @click="props.onContinueWithoutSaving"
            type="button"
            v-if="props.onContinueWithoutSaving"
          >
            <ArrowImport class="absolute right-4 top-2 mr-2 size-6 lg:top-3" />
            {{ $t('LoginPopup.button.ContinueWithoutEmail') }}
          </button>
        </div>
      </template>
    </LoginForm>
  </div>
</template>
